/**
 * Airport Delay/Disruption data source.
 * Tracks delays and closures at major airports from the configured
 * airports list (128 airports in airports.ts).
 * Uses FAA API for US airports and EUROCONTROL-style data for Europe.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'airport-delays';

interface AirportDelay {
  icao: string;
  iata: string;
  name: string;
  lat: number;
  lng: number;
  country: string;
  countryCode: string;
  delayMinutes: number;
  status: 'normal' | 'delay' | 'closure' | 'ground_stop';
  reason?: string;
  updatedAt: string;
}

interface AirportDelayResponse {
  delays: AirportDelay[];
}

function delayToSeverity(delay: AirportDelay): Severity {
  if (delay.status === 'closure' || delay.status === 'ground_stop') return 'critical';
  if (delay.delayMinutes >= 120) return 'high';
  if (delay.delayMinutes >= 60) return 'medium';
  if (delay.delayMinutes >= 30) return 'low';
  return 'info';
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const airportDelaySource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Airport Delays',
    description: 'Airport delay and disruption data for 128 major airports',
    icon: 'plane',
    category: 'infrastructure',
    updateInterval: 300_000, // 5 minutes
    requiresApiKey: false,
    dataLicense: 'FAA / Various',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Airport delay source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/airport-delays`;
      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`Airport delay API returned ${response.status}`);
      }

      const data: AirportDelayResponse = await response.json();

      if (!data.delays || !Array.isArray(data.delays)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      // Only emit events for airports with actual delays/disruptions
      for (const delay of data.delays) {
        if (delay.status === 'normal' && delay.delayMinutes < 15) continue;

        const statusLabel = delay.status === 'ground_stop' ? 'GROUND STOP'
          : delay.status === 'closure' ? 'CLOSED'
          : delay.delayMinutes > 0 ? `${delay.delayMinutes}min delay`
          : 'Disrupted';

        events.push({
          id: `airport-${delay.icao}-${new Date().toISOString().slice(0, 10)}`,
          source: SOURCE_ID,
          timestamp: delay.updatedAt,
          type: 'infrastructure',
          title: `${delay.iata} (${delay.name}): ${statusLabel}${delay.reason ? ` — ${delay.reason}` : ''}`,
          severity: delayToSeverity(delay),
          location: {
            lat: delay.lat,
            lng: delay.lng,
            name: delay.name,
            country: delay.country,
            countryCode: delay.countryCode,
          },
          tags: ['airport', 'delay', delay.iata.toLowerCase(), delay.icao.toLowerCase()],
          raw: {
            icao: delay.icao,
            iata: delay.iata,
            delayMinutes: delay.delayMinutes,
            status: delay.status,
            reason: delay.reason,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
